import React, { useEffect, useState } from 'react';
import { FragmentInstance } from '@moment/shared-code/widgets.tsx';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getTopInterests } from '@moment/local-personalization/scorer.ts';
import { LiveFragmentRenderer } from './LiveFragmentRenderer';
import { EnhancedLightboxItem } from '@moment/net/softbox-types.ts';
import { generatePersonalizedFetchFeedQuery } from '@moment/net/personlized-feed-fetch-query.ts';
import StoryMediumSkeleton from '@moment/components/StoryMedium/StoryMediumSkeleton.tsx';

interface PersonalizedLiveFragmentRendererProps {
  fragment: FragmentInstance;
  pageData: any;
}

/**
 * A wrapper for LiveFragmentRenderer that handles client-side fetching
 * for personalized feeds based on user affinity scores from localStorage.
 */
export const PersonalizedLiveFragmentRenderer: React.FC<
  PersonalizedLiveFragmentRendererProps
> = ({ fragment, pageData }) => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [interestIds, setInterestIds] = useState<string[]>([]);
  const [isClient, setIsClient] = useState(false);

  // This effect runs only on the client, after hydration.
  useEffect(() => {
    setIsClient(true);
    // Fetch top interests from localStorage.
    const topInterests = getTopInterests(3); // Fetch top 3 interests. currently softbox max=3
    setInterestIds(topInterests);
  }, []);

  // Fetch the personalized feed using the interest IDs.
  // This query is disabled until we are on the client and have interest IDs.
  const {
    data: feedData,
    isLoading,
  } = useQuery({
    queryKey: ['feed', 'personalized', interestIds, language],
    queryFn: generatePersonalizedFetchFeedQuery(interestIds)({
      lang: language,
      logger: {
        log: console.log,
      }
    }),
    enabled: isClient,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  if (isLoading) {
    // Show a shimmer/skeleton UI that mimics the content.
    // Rendering a few gives a better preview of the feed.
    return (
      <div className="feed-container" style={{marginLeft: 20, marginRight: 20}}>
        <StoryMediumSkeleton />
        <StoryMediumSkeleton />
      </div>
    );
  }

  return (
    <LiveFragmentRenderer
      fragment={fragment}
      pageData={pageData}
      isLoading={isLoading}
      newsItems={(feedData?.news?.items ?? []).map(
        (i) => new EnhancedLightboxItem(i)
      )}
      interests={feedData?.interests || {}}
      source="personalized-feed"
    />
  );
};
